import Box from '@mui/material/Box';
import Stack from '@mui/material/Stack';
import { styled } from '@mui/material/styles';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import { useCallback, useMemo, useState } from 'react';

import { formatCurrency } from '@/helpers/formatters';
import type { FinanceTerm } from '@/types/FinanceHelperTypes';
import type { NonEmptyArray } from '@/types/utilityTypes';

type AvailableTermsProps = {
  terms: NonEmptyArray<FinanceTerm>;
};

const TermsContainer = styled(Box)(({ theme }) => ({
  padding: theme.spacing(2),
  margin: theme.spacing(2, 0),
  borderRadius: theme.shape.borderRadius,
  backgroundColor: theme.palette.info.faint,
}));

const AvailableTerms = ({ terms }: AvailableTermsProps) => {
  const hasMultipleTerms: boolean = terms.length > 1;
  const [selectedTerm, setSelectedTerm] = useState(terms[0]);
  const [selectedTermLength, setSelectedTermLength] = useState(terms[0].length);

  const sortedTerms = [...terms].sort((a, b) => a.length - b.length);

  const selectedTermRate = useMemo(() => {
    const maxPayment = formatCurrency(selectedTerm.maxTermRate.monthlyPayment);
    const minPayment = formatCurrency(selectedTerm.minTermRate.monthlyPayment);
    const termRate =
      (maxPayment == minPayment ? maxPayment : `${minPayment}-${maxPayment}`) +
      '/month';

    return termRate;
  }, [selectedTerm]);

  const selectedTermAPR = useMemo(() => {
    const maxApr = selectedTerm.maxTermRate.apr;
    const minApr = selectedTerm.minTermRate.apr;

    const termAPR =
      (minApr == maxApr ? minApr : `${minApr}-${maxApr}`) + '% APR';

    return termAPR;
  }, [selectedTerm]);

  const handleDropdownChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const value = Number(e.target.value);
      const term = terms.find((fbsTerm) => fbsTerm.length == value)!;
      setSelectedTermLength(value);
      setSelectedTerm(term);
    },
    [terms],
  );

  return (
    <TermsContainer className="kmx-car-tile__available-terms">
      {hasMultipleTerms && (
        <TextField
          select
          fullWidth
          label="Term length"
          value={selectedTermLength}
          onChange={handleDropdownChange}
          className="available-terms-dropdown"
          slotProps={{
            select: {
              native: true,
            },
          }}
        >
          {sortedTerms.map((term) => (
            <option key={term.length} value={term.length}>
              {`${term.length} months`}
            </option>
          ))}
        </TextField>
      )}

      <Box className="selected-term-information">
        <Typography variant="h5" mt={2}>
          {selectedTermRate}
        </Typography>

        <Stack direction="row" gap={0.5}>
          <Typography variant="body2"> {selectedTermAPR} </Typography>
          {hasMultipleTerms && (
            <>
              <span style={{ fontSize: '12px' }}>&#8226;</span>
              <Typography variant="body2">
                {selectedTermLength} months
              </Typography>
            </>
          )}
        </Stack>
      </Box>
    </TermsContainer>
  );
};

export default AvailableTerms;
